import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { MuiOtpInput } from "mui-one-time-password-input";
import React, { useEffect, useState } from "react";
import { getRelativeFontSize, mediumFont, regularFont } from "utils/styles";
import { resendOTP, validateOTP } from "./AuthServices";

const BRAND = "#f8481c";
const BRAND_LIGHT = "#ff6b4a";

interface Props {
  open: boolean;
  token: string;
  email: string;
  onClose: () => void;
  onVerified: (response: any) => void;
}

const ValidateOTP: React.FC<Props> = ({
  open,
  token,
  email,
  onClose,
  onVerified,
}) => {
  const theme = useTheme();
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [resendDisabled, setResendDisabled] = useState(true);
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    if (open) {
      setOtp("");
      startTimer();
      setServerError("");
    }
  }, [open]);

  useEffect(() => {
    let interval: any = null;
    if (resendDisabled && timer > 0) {
      interval = setInterval(() => {
        setTimer((t) => t - 1);
      }, 1000);
    } else if (timer === 0) {
      setResendDisabled(false);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [resendDisabled, timer]);

  const startTimer = () => {
    setResendDisabled(true);
    setTimer(60);
  };

  const handleVerify = async () => {
    if (!otp || otp.length < 4) {
      setServerError("Please enter the OTP sent to your email");
      return;
    }
    setLoading(true);
    setServerError("");
    try {
      const resp: any = await validateOTP(token, email, otp);
      onVerified(resp);
      onClose();
    } catch (error: any) {
      setServerError(error?.message || error?.errorMessage || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setServerError("");
    setResendDisabled(true);
    startTimer();
    try {
      await resendOTP(email);
    } catch (error: any) {
      setServerError(
        error?.message || error?.errorMessage || "Unable to resend OTP"
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: `0 24px 64px ${alpha(
            theme.palette.common.black,
            theme.palette.mode === "dark" ? 0.4 : 0.12
          )}`,
          background: theme.palette.background.paper,
        },
      }}
    >
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
        }}
      />
      <DialogTitle
        sx={{
          ...mediumFont,
          fontSize: getRelativeFontSize(3),
          color: theme.palette.text.primary,
          pb: 0,
        }}
      >
        Enter OTP
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography
          variant="body2"
          sx={{
            ...regularFont,
            fontSize: getRelativeFontSize(0),
            color: theme.palette.text.secondary,
            mb: 2,
          }}
        >
          Enter the one-time password sent to <strong>{email}</strong>
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <MuiOtpInput
            value={otp}
            onChange={(val: string) => setOtp(val)}
            length={4}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                "& fieldset": {
                  borderColor: alpha(theme.palette.divider, 0.3),
                },
                "&:hover fieldset": {
                  borderColor: alpha(BRAND, 0.5),
                },
                "&.Mui-focused fieldset": {
                  borderColor: BRAND,
                  borderWidth: 2,
                  boxShadow: `0 0 0 4px ${alpha(BRAND, 0.12)}`,
                },
              },
              "& .MuiInputBase-input": {
                textAlign: "center",
                fontSize: 20,
                color: theme.palette.text.primary,
              },
            }}
          />
        </Box>

        {serverError && (
          <Typography
            color="error"
            variant="body2"
            sx={{
              mb: 1,
              ...regularFont,
              fontSize: getRelativeFontSize(0),
            }}
          >
            {serverError}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Button
            onClick={handleResend}
            disabled={resendDisabled}
            sx={{
              ...regularFont,
              fontSize: getRelativeFontSize(0),
              textTransform: "none",
              color: theme.palette.text.secondary,
              "&:hover": {
                color: BRAND,
                backgroundColor: alpha(BRAND, 0.06),
              },
            }}
          >
            {resendDisabled ? `Resend OTP (${timer}s)` : "Resend OTP"}
          </Button>
          <Button
            onClick={onClose}
            sx={{
              ...regularFont,
              fontSize: getRelativeFontSize(0),
              textTransform: "none",
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: alpha(theme.palette.divider, 0.1),
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        <Button
          onClick={handleVerify}
          disabled={loading}
          variant="contained"
          sx={{
            ...mediumFont,
            fontSize: getRelativeFontSize(1),
            textTransform: "none",
            px: 3,
            py: 1.25,
            borderRadius: "10px",
            background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
            boxShadow: `0 4px 20px ${alpha(BRAND, 0.35)}`,
            "&:hover": {
              background: `linear-gradient(135deg, ${BRAND_LIGHT} 0%, ${BRAND} 100%)`,
              boxShadow: `0 6px 24px ${alpha(BRAND, 0.45)}`,
            },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Verify"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidateOTP;
